import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "../components/Navbar";
import "../styles/ManageAccount.css";
import { useTranslation } from "react-i18next";

interface EmailRecord {
  personEmailAddressID: number;
  email: string;
  inuse: number; // 1 if in use, 0 if not
  primaryEmail: number; // 1 if primary, 0 if not
  dateCreated: string;
}

interface PhoneRecord {
  personPhoneID: number;
  phoneNumber: string;
  phoneType: "mobile" | "home" | "business";
  inUse: number; // 1 if in use, 0 if not
  primaryPhone: number; // 1 if primary, 0 if not
  dateCreated: string;
}

interface AccountDetails {
  personID: number;
  givenNames: string;
  familyName: string;
  emails: EmailRecord[];
  phoneNumbers: PhoneRecord[];
}

interface SaveStatus {
  message: string;
  isError: boolean;
}

const ManageAccount: React.FC = () => {
  const { t } = useTranslation("translation");
  // Helper alias to bypass TS issues with t()
  const translate = t as unknown as (key: string, options?: any) => string;

  // State for personal details
  const [givenNames, setGivenNames] = useState("");
  const [familyName, setFamilyName] = useState("");
  const [emails, setEmails] = useState<EmailRecord[]>([]);
  const [phoneNumbers, setPhoneNumbers] = useState<PhoneRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [saveStatus, setSaveStatus] = useState<SaveStatus | null>(null);

  // State for the "Add Email" modal
  const [showAddEmailModal, setShowAddEmailModal] = useState(false);
  const [newEmail, setNewEmail] = useState("");
  const [emailError, setEmailError] = useState("");

  // State for the "Add Phone" modal
  const [showAddPhoneModal, setShowAddPhoneModal] = useState(false);
  const [newPhone, setNewPhone] = useState("");
  const [newPhoneType, setNewPhoneType] = useState<"mobile" | "home" | "business">("mobile");
  const [phoneError, setPhoneError] = useState("");

  // Retrieve oneLoginSub from query string or localStorage
  const searchParams = new URLSearchParams(window.location.search);
  const oneLoginSub = localStorage.getItem("oneLoginSub") || "";

  useEffect(() => {
    // Fetch account details from API
    fetch(`https://api.psanalytics.co.uk/ncjp/getAccountDetails.php?oneLoginSub=${encodeURIComponent(oneLoginSub)}`, {
      credentials: "include",
    })
      .then((res) => res.json())
      .then((data: { status: string; data: AccountDetails; message?: string }) => {
        console.log("Fetched account details:", data);
        if (data.status === "success" && data.data) {
          setGivenNames(data.data.givenNames || "");
          setFamilyName(data.data.familyName || "");
          setEmails(data.data.emails || []);
          setPhoneNumbers(data.data.phoneNumbers || []);
        } else {
          console.error("Error retrieving account details:", data.message);
        }
        setIsLoading(false);
      })
      .catch((err) => {
        console.error("Error fetching account details:", err);
        setIsLoading(false);
      });
  }, [oneLoginSub]);

  // Handle form submission to update account details.
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const payload = { givenNames, familyName };
    fetch(`https://api.psanalytics.co.uk/ncjp/updateAccountDetails.php?oneLoginSub=${encodeURIComponent(oneLoginSub)}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Account details updated:", data);
        if (data.status === "success") {
          setSaveStatus({ message: translate("accountUpdatedSuccessfully") || "Account details updated successfully!", isError: false });
        } else {
          setSaveStatus({ message: (translate("errorUpdatingAccountDetails") || "Error updating account details: ") + data.message, isError: true });
        }
      })
      .catch((err) => {
        console.error("Error updating account details:", err);
        setSaveStatus({ message: translate("errorUpdatingAccountDetails") || "Error updating account details. Please try again.", isError: true });
      });
  };

  // Email management handlers
  const handleEmailInUseChange = (index: number, inUse: boolean) => {
    if (emails[index].primaryEmail === 1) return; // primary email must remain in use
    const updatedEmails = [...emails];
    updatedEmails[index].inuse = inUse ? 1 : 0;
    setEmails(updatedEmails);
  };

  const handlePrimaryEmailChange = (index: number) => {
    const updatedEmails = emails.map((email, i) => ({
      ...email,
      primaryEmail: i === index ? 1 : 0,
      inuse: i === index ? 1 : email.inuse, // ensure primary email is in use
    }));
    setEmails(updatedEmails);
  };

  // Phone management handlers
  const handlePhoneInUseChange = (index: number, inUse: boolean) => {
    if (phoneNumbers[index].primaryPhone === 1) return; // primary phone must remain in use
    const updatedPhones = [...phoneNumbers];
    updatedPhones[index].inUse = inUse ? 1 : 0;
    setPhoneNumbers(updatedPhones);
  };

  const handlePrimaryPhoneChange = (index: number) => {
    const updatedPhones = phoneNumbers.map((phone, i) => ({
      ...phone,
      primaryPhone: i === index ? 1 : 0,
      inUse: i === index ? 1 : phone.inUse, // ensure primary phone is in use
    }));
    setPhoneNumbers(updatedPhones);
  };

  // Simple email validation function
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(email);
  };

  // Simple phone validation function
  const validatePhone = (phone: string): boolean => {
    const re = /^[0-9+\s-]+$/;
    return re.test(phone) && phone.trim().length >= 7;
  };

  // Handler for adding a new email
  const handleAddEmail = () => {
    if (!newEmail) {
      console.log("No email entered; aborting add email.");
      return;
    }
    if (!validateEmail(newEmail)) {
      setEmailError(translate("invalidEmail") || "Please enter a valid email address.");
      return;
    }
    setEmailError("");
    console.log("Attempting to add new email:", newEmail);
    fetch(`https://api.psanalytics.co.uk/ncjp/addEmail.php?oneLoginSub=${encodeURIComponent(oneLoginSub)}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email: newEmail }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Email added API response:", data);
        if (data.status === "success") {
          const newEmailRecord: EmailRecord = {
            personEmailAddressID: Number(data.newEmailID),
            email: newEmail,
            inuse: 1,
            primaryEmail: 0,
            dateCreated: new Date().toISOString(),
          };
          setEmails([...emails, newEmailRecord]);
          setShowAddEmailModal(false);
          setNewEmail("");
        } else {
          console.error("Error adding email:", data.message);
          setSaveStatus({ message: translate("errorAddingEmail") || "Error adding email: " + data.message, isError: true });
        }
      })
      .catch((err) => {
        console.error("Error during add email fetch:", err);
        setSaveStatus({ message: translate("errorAddingEmail") || "Error adding email. Please try again.", isError: true });
      });
  };

  // Handler for adding a new phone
  const handleAddPhone = () => {
    if (!newPhone) {
      console.log("No phone entered; aborting add phone.");
      return;
    }
    if (!validatePhone(newPhone)) {
      setPhoneError(translate("invalidPhone") || "Please enter a valid phone number.");
      return;
    }
    setPhoneError("");
    console.log("Attempting to add new phone:", newPhone, "Type:", newPhoneType);
    fetch(`https://api.psanalytics.co.uk/ncjp/addPhoneNumber.php?oneLoginSub=${encodeURIComponent(oneLoginSub)}`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ phoneNumber: newPhone, phoneType: newPhoneType }),
    })
      .then((res) => res.json())
      .then((data) => {
        console.log("Phone added API response:", data);
        if (data.status === "success") {
          const newPhoneRecord: PhoneRecord = {
            personPhoneID: Number(data.newPhoneID),
            phoneNumber: newPhone,
            phoneType: newPhoneType,
            inUse: 1,
            primaryPhone: 0,
            dateCreated: new Date().toISOString(),
          };
          setPhoneNumbers([...phoneNumbers, newPhoneRecord]);
          setShowAddPhoneModal(false);
          setNewPhone("");
          setNewPhoneType("mobile");
        } else {
          console.error("Error adding phone:", data.message);
          setSaveStatus({ message: translate("errorAddingPhone") || "Error adding phone: " + data.message, isError: true });
        }
      })
      .catch((err) => {
        console.error("Error during add phone fetch:", err);
        setSaveStatus({ message: translate("errorAddingPhone") || "Error adding phone. Please try again.", isError: true });
      });
  };

  if (isLoading) {
    return (
      <div>
        <Navbar />
        <main className="account-container">
          <p>{translate("loadingAccountDetails") || "Loading account details..."}</p>
        </main>
      </div>
    );
  }

  return (
    <div>
      <Navbar />
      <main className="account-container">
        <h1>{translate("manageAccountTitle") || "Manage My Account"}</h1>
        <section className="account-details">
          <h2>{translate("personalInformation") || "Personal Information"}</h2>
          <form onSubmit={handleSubmit}>
            <table className="account-table">
              <tbody>
                <tr>
                  <th colSpan={2}>{translate("nameHeader") || "Name"}</th>
                </tr>
                <tr>
                  <td>
                    <label htmlFor="forename">{translate("forenameLabel") || "Forename / Given Name:"}</label>
                  </td>
                  <td>
                    <input
                      type="text"
                      id="forename"
                      name="forename"
                      value={givenNames}
                      onChange={(e) => setGivenNames(e.target.value)}
                    />
                  </td>
                </tr>
                <tr>
                  <td>
                    <label htmlFor="surname">{translate("surnameLabel") || "Surname / Family Name:"}</label>
                  </td>
                  <td>
                    <input
                      type="text"
                      id="surname"
                      name="surname"
                      value={familyName}
                      onChange={(e) => setFamilyName(e.target.value)}
                    />
                  </td>
                </tr>
                <tr className="section-spacing">
                  <td colSpan={2}></td>
                </tr>
                <tr>
                  <th colSpan={2}>{translate("emailAddresses") || "Email Addresses"}</th>
                </tr>
                {emails.map((email, index) => (
                  <tr key={email.personEmailAddressID}>
                    <td>{email.email}</td>
                    <td>
                      <div className="email-controls">
                        <label>
                          {translate("inUse") || "In Use:"}
                          <input
                            type="checkbox"
                            checked={email.inuse === 1}
                            onChange={(e) => handleEmailInUseChange(index, e.target.checked)}
                            disabled={email.primaryEmail === 1}
                          />
                        </label>
                        <label>
                          {translate("primary") || "Primary:"}
                          <input
                            type="radio"
                            name="primaryEmail"
                            checked={email.primaryEmail === 1}
                            onChange={() => handlePrimaryEmailChange(index)}
                          />
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2}>
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      onClick={() => {
                        console.log("Add Email button clicked");
                        setShowAddEmailModal(true);
                      }}
                    >
                      {translate("addEmail") || "Add Email"}
                    </button>
                  </td>
                </tr>
                <tr className="section-spacing">
                  <td colSpan={2}></td>
                </tr>
                <tr>
                  <th colSpan={2}>{translate("phoneNumbers") || "Phone Numbers"}</th>
                </tr>
                {phoneNumbers.map((phone, index) => (
                  <tr key={phone.personPhoneID}>
                    <td>
                      {phone.phoneNumber} ({phone.phoneType})
                    </td>
                    <td>
                      <div className="phone-controls">
                        <label>
                          {translate("inUse") || "In Use:"}
                          <input
                            type="checkbox"
                            checked={phone.inUse === 1}
                            onChange={(e) => handlePhoneInUseChange(index, e.target.checked)}
                            disabled={phone.primaryPhone === 1}
                          />
                        </label>
                        <label>
                          {translate("primary") || "Primary:"}
                          <input
                            type="radio"
                            name="primaryPhone"
                            checked={phone.primaryPhone === 1}
                            onChange={() => handlePrimaryPhoneChange(index)}
                          />
                        </label>
                      </div>
                    </td>
                  </tr>
                ))}
                <tr>
                  <td colSpan={2}>
                    <button
                      type="button"
                      className="govuk-button govuk-button--secondary"
                      onClick={() => {
                        console.log("Add Phone button clicked");
                        setShowAddPhoneModal(true);
                      }}
                    >
                      {translate("addPhone") || "Add Phone"}
                    </button>
                  </td>
                </tr>
                <tr className="section-spacing">
                  <td colSpan={2}></td>
                </tr>
              </tbody>
            </table>
            {saveStatus && (
              <div className={`save-message ${saveStatus.isError ? "save-error" : "save-success"}`}>
                {saveStatus.message}
              </div>
            )}
            <div className="button-group">
              <button type="submit" className="govuk-button">
                {translate("save") || "Save"}
              </button>
              <Link to={`/`} className="govuk-button govuk-button--secondary cancel-button">
                {translate("cancel") || "Cancel"}
              </Link>
            </div>
          </form>
        </section>
      </main>

      {/* Modal for adding a new email */}
      {showAddEmailModal && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h2>{translate("addEmailTitle") || "Add New Email"}</h2>
            <p className="modal-instructions">
              {translate("addEmailInstructions") ||
                "Please input your new email address, you will be asked to verify it through an email which will be sent to you. You will be asked to set your primary default email when you are returned to the main screen."}
            </p>
            <input
              type="email"
              className="modal-input"
              placeholder={translate("emailPlaceholder") || "Enter new email address"}
              value={newEmail}
              onChange={(e) => {
                console.log("New email input changed:", e.target.value);
                setNewEmail(e.target.value);
              }}
            />
            {emailError && <p className="modal-error">{emailError}</p>}
            <div className="button-group">
              <button
                type="button"
                className={`govuk-button ${!validateEmail(newEmail) ? "disabled-button" : ""}`}
                onClick={() => {
                  console.log("Save button clicked. newEmail:", newEmail);
                  handleAddEmail();
                }}
                disabled={!validateEmail(newEmail)}
              >
                {translate("save") || "Save"}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                onClick={() => {
                  console.log("Add email modal cancelled.");
                  setShowAddEmailModal(false);
                }}
              >
                {translate("cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal for adding a new phone */}
      {showAddPhoneModal && (
        <div className="modal-overlay">
          <div className="modal-dialog">
            <h2>{translate("addPhoneTitle") || "Add New Phone"}</h2>
            <p className="modal-instructions">
              {translate("addPhoneInstructions") ||
                "Please enter your new phone number. Choose the phone type below."}
            </p>
            <input
              type="tel"
              className="modal-input"
              placeholder={translate("phonePlaceholder") || "Enter new phone number"}
              value={newPhone}
              onChange={(e) => {
                console.log("New phone input changed:", e.target.value);
                setNewPhone(e.target.value);
              }}
            />
            <select
              className="modal-input"
              value={newPhoneType}
              onChange={(e) => setNewPhoneType(e.target.value as "mobile" | "home" | "business")}
            >
              <option value="mobile">{translate("mobile") || "Mobile"}</option>
              <option value="home">{translate("home") || "Home"}</option>
              <option value="business">{translate("business") || "Business"}</option>
            </select>
            {phoneError && <p className="modal-error">{phoneError}</p>}
            <div className="button-group">
              <button
                type="button"
                className={`govuk-button ${!validatePhone(newPhone) ? "disabled-button" : ""}`}
                onClick={() => {
                  console.log("Save phone button clicked. New phone:", newPhone, "Type:", newPhoneType);
                  handleAddPhone();
                }}
                disabled={!validatePhone(newPhone)}
              >
                {translate("save") || "Save"}
              </button>
              <button
                type="button"
                className="govuk-button govuk-button--secondary"
                onClick={() => {
                  console.log("Add phone modal cancelled.");
                  setShowAddPhoneModal(false);
                }}
              >
                {translate("cancel") || "Cancel"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ManageAccount;
